import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageCircle } from "lucide-react";

import {
  Earned,
  EarnedTooltip,
  TotalBudget,
} from "@/components/CampaignAnalytics";
import { OutboundCampaignRow } from "@/components/OutboundCampaignRow";
import { Spinner } from "@/components/Spinner";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { campaignsQueryOptions, inboundCampaignsQueryOptions } from "@/lib/api";

export const Route = createFileRoute(
  "/app/_sidebar-layout/_campaigns-layout/campaigns/",
)({
  component: HomeComponent,
  loader: ({ context: { queryClient } }) => {
    void Promise.all([
      queryClient.prefetchQuery(campaignsQueryOptions()),
      queryClient.prefetchQuery(inboundCampaignsQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Campaigns" }] }),
  pendingComponent: () => (
    <div className="p-6 flex items-center justify-center">
      <Spinner delay={1000} />
    </div>
  ),
});

function HomeComponent() {
  const { data: outbound } = useSuspenseQuery(campaignsQueryOptions());
  const { data: inbound } = useSuspenseQuery(inboundCampaignsQueryOptions());

  const activeInboundCampaigns = inbound.filter(
    (item) => !["proposed"].includes(item.campaign_status.state),
  );

  const activeOutboundCampaigns = outbound.filter((item) =>
    item.algorithms.some((algo) => algo.details.state === "active"),
  );
  const pendingOutboundCampaigns = outbound.filter((item) =>
    item.algorithms.some(
      (algo) =>
        algo.details.state === "proposed" || algo.details.state === "accepted",
    ),
  );

  if (
    activeInboundCampaigns.length === 0 &&
    pendingOutboundCampaigns.length + activeOutboundCampaigns.length === 0
  ) {
    return (
      <div className="p-6 flex items-center justify-center text-sm text-slate-dim">
        No active campaigns found
      </div>
    );
  }

  return (
    <>
      {activeInboundCampaigns.length > 0 && (
        <>
          <div className="bg-slate-subtle text-xl flex items-center p-4 border-b border-slate-6 dark:border-slatedark-6">
            On your feeds
          </div>

          {activeInboundCampaigns.map((item) => {
            if (!item.campaign_status.algorithm) {
              return null;
            }

            const campaignId = item.id.toString();
            const algoId = item.campaign_status.algorithm.id.toString();

            return (
              <Link
                key={item.id}
                to="/app/campaigns/$campaignId/review/$algoId"
                params={{ campaignId, algoId }}
                className="hover:bg-slate-2 dark:hover:bg-slatedark-2 p-4 text-left w-full not-last:border-b border-slate-6 dark:border-slatedark-6 flex flex-col gap-4 items-start lg:flex-row justify-between lg:items-center lg:gap-2"
              >
                <div className="flex items-center gap-3">
                  <div className="h-4 w-4 bg-green-10 dark:bg-greendark-10 rounded-full shadow-xs" />
                  <div className="font-semibold text-lg">
                    {item.display_name}
                  </div>
                  <div className="rounded-md px-2 py-0.5 text-sm bg-slate-action text-slate-normal">
                    {item.campaign_status.algorithm.display_name}
                  </div>
                </div>

                <div className="flex gap-2">
                  <SimpleTooltip message="Impressions">
                    <div className="text-slate-normal bg-slate-action px-3 py-1 rounded-md">
                      {item.campaign_status.impressions_to_date || 0}
                      <span className="text-slate-dim">
                        {" / "}
                        {item.campaign_status.impressions_budget +
                          (item.campaign_status.impressions_to_date || 0)}
                      </span>
                    </div>
                  </SimpleTooltip>
                  <div className="text-green-normal bg-green-action px-3 py-1 rounded-md">
                    <EarnedTooltip status={item.campaign_status}>
                      <Earned status={item.campaign_status} />{" "}
                      <span className="text-green-normal">
                        {"/ "}
                        <TotalBudget status={item.campaign_status} />
                      </span>
                    </EarnedTooltip>
                  </div>
                  {Boolean(item.campaign_status.unread_message_count) && (
                    <Link
                      to="/app/campaigns/$campaignId/feed/$algoId/messages"
                      params={{ campaignId, algoId }}
                      className="flex items-center gap-1 text-red-dim bg-red-action px-2 py-1 rounded-md cursor-pointer-extra"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <div className="text-sm">
                        {item.campaign_status.unread_message_count}
                      </div>
                    </Link>
                  )}
                </div>
              </Link>
            );
          })}
        </>
      )}
      {pendingOutboundCampaigns.length + activeOutboundCampaigns.length > 0 && (
        <>
          <div className="bg-slate-subtle text-xl flex items-center p-4 border-b border-slate-6 dark:border-slatedark-6">
            Your campaigns
          </div>
          {activeOutboundCampaigns.map((item) => {
            return <OutboundCampaignRow item={item} key={item.id} />;
          })}
          {pendingOutboundCampaigns.map((item) => {
            return <OutboundCampaignRow item={item} key={item.id} />;
          })}
        </>
      )}
    </>
  );
}
